import React, { lazy, Suspense, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ErrorBoundary from '@fs/zion-error-boundary'
import { Title } from '@fs/zion-head'
import { baseUrl } from '@fs/zion-config'
import { useRoleIndicator } from '@fs/zion-role-indicator'
import { NotFound, Redirect, Route, Switch, useQueryParams } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { colors, useOverlay } from '@fs/zion-ui'
import CustomAuthRoute from './components/CustomAuthRoute'
import { accountSetupFlowSteps, addNameFlowSteps, ordinancesReadySteps } from './components/fnaReducer'
import FtteProvider from './components/FtteProvider/FtteProvider'
import { removeFlagsButton, removeUsabillaButton } from './lib/helpers'
import { getMemberData } from './lib/services'
import { useNavigate } from './hooks'
import TryDemoWelcomeOverlay from './components/common/overlays/TryDemoWelcomeOverlay'
import { tryDemoSessionCache } from './lib/tryDemoServices'
import Header from './components/common/Header/Header'
import PageSpinner from './components/common/PageSpinner'
import RoleIndicatorHeader from './components/common/RoleIndicatorHeader'
import FnaUsabilla from './components/common/FnaUsabilla'
import ChooseYourDemo from './components/Previews/ChooseYourDemo/ChooseYourDemo'

const FamilyNameAssist = lazy(() => import('./pages/FamilyNameAssist'))
const LcrAuthSwitch = lazy(() => import('./pages/LcrAuthSwitch'))
const LcrDemo = lazy(() => import('./pages/LcrDemo'))
const NameCards = lazy(() => import('./pages/pdf/NameCards'))
const QrScan = lazy(() => import('./pages/QrScan'))
const VerifyAccount = lazy(() => import('./pages/VerifyAccount'))

// PDF Pages
const MemberInstructions = lazy(() => import('./pages/pdf/MemberInstructions'))
const PlanningConsiderations = lazy(() => import('./pages/pdf/PlanningConsiderations'))

// Preview Pages
const LandingPreview = lazy(() => import('./pages/Previews/LandingPreview'))
const MemberPreview = lazy(() => import('./pages/Previews/MemberPreview'))
const GetStarted = lazy(() => import('./components/Previews/LandingPreview/GetStarted/GetStarted'))
const GetStartedHelper1 = lazy(() => import('./components/Previews/LandingPreview/GetStarted/GetStartedHelper1'))
const GetStartedHelper2 = lazy(() => import('./components/Previews/LandingPreview/GetStarted/GetStartedHelper2'))

function App({ hideFSHF, setHideFSHF }) {
  const [t] = useTranslation()
  const { setRole } = useRoleIndicator()
  const { signedIn } = useUser()
  const { isTryDemo, location, onPush } = useNavigate()
  const { query, removeQuery } = useQueryParams()
  const tabText = t('app-name', 'Family Name Assist')
  const tryDemoRoleIndicatorExitText = t('tryDemo.role-indicator-exit-text', 'Exit Demo')
  const isIntegration = baseUrl.includes('integration')

  // EAACOMPL-978: This fixes a keyboard trap where the `tryDemoWelcomeOverlay` would not focus back to the page header after closing.
  const focusBackRef = useRef(null)
  const tryDemoWelcomeOverlay = useOverlay({ focusBackRef })
  useEffect(() => {
    // The TryDemoWelcomeOverlay is opened on the DecisionTree page, so we check for the heading until it is rendered.
    if (isTryDemo && focusBackRef.current === null) {
      const intervalId = setInterval(() => {
        // NOTE: The data-testid for the header on DecisionTree page is `page-header.undefined` because that page doesn't have a `step` URL param.
        const pageHeaderEl = document.querySelector('[data-testid="page-header.undefined"]')
        if (pageHeaderEl) {
          focusBackRef.current = pageHeaderEl
          clearInterval(intervalId)
        }
      }, 100)

      return () => clearInterval(intervalId)
    }

    return null
  }, [isTryDemo])

  // Remove SplitIO Flags button if a query param `hideExtraButtons=true` is in the URL
  useEffect(() => {
    let flagsCount = 0
    let usabillaCount = 0

    const pollinglyRemoveFlagsButton = () => {
      try {
        removeFlagsButton()
      } catch {
        if (flagsCount++ < 50) setTimeout(() => pollinglyRemoveFlagsButton(), 100)
      }
    }

    const pollinglyRemoveUsabillaButton = () => {
      try {
        removeUsabillaButton()
      } catch {
        if (usabillaCount++ < 50) setTimeout(() => pollinglyRemoveUsabillaButton(), 100)
      }
    }

    // We only need to remove the button if the value is 'true'
    if (query.hideExtraButtons === 'true') {
      pollinglyRemoveFlagsButton()
      pollinglyRemoveUsabillaButton()
    }
  }, [query.hideExtraButtons])

  // Remove `memberUuid` query parameter if it is identical to the `memberPerson?.uuid`
  useEffect(() => {
    if (signedIn && query.memberUuid) {
      // NOTE: This call is made to get the helper's UUID, that is why we do not pass in the memberUuid variable.
      getMemberData().then((res) => {
        if (query.memberUuid === res.data.personUuid) removeQuery('memberUuid')
      })
    }
  }, [query.memberUuid, removeQuery, signedIn])

  const getReplaceParamsForTryDemoExit = useCallback(
    (url) => {
      const { search } = location
      const params = new URLSearchParams(search)
      params.delete('tryDemo')
      params.delete('fromMember')
      // using isExternal fixes header being hidden on exit
      return [`${url}${query.fromMember ? '/members' : ''}`, null, true, false, params.toString()]
    },
    [location, query.fromMember]
  )

  const onExit = useCallback(() => {
    tryDemoSessionCache.clear()
    const onReplaceParams = getReplaceParamsForTryDemoExit('/family-name-assist')
    onPush(...onReplaceParams)
    setRole({})
  }, [getReplaceParamsForTryDemoExit, onPush, setRole])

  const toggleRoleIndicator = useCallback(() => {
    setRole(() => {
      return {
        text: <RoleIndicatorHeader />,
        color: colors.yellow40,
        onExit,
        exitTitle: tryDemoRoleIndicatorExitText,
      }
    })
  }, [onExit, setRole, tryDemoRoleIndicatorExitText])

  useEffect(() => {
    if (isTryDemo) toggleRoleIndicator()
  }, [toggleRoleIndicator, isTryDemo])

  // #region Redirects
  const redirectsConfig = [
    {
      jest: 'old-leader-preview-page-to-current',
      from: '/leader-preview-new',
      to: '/leader-preview',
    },
    {
      jest: 'old-leader-preview-steps-page-to-current',
      from: '/leader-preview-new/steps',
      to: '/leader-preview',
    },
    {
      jest: 'assist-page-to-preview-for-leaders-and-helpers',
      from: '/assist/:memberUuid?',
      to: '/family-name-assist',
    },
    {
      jest: 'old-leader-preview-page-to-new-fna',
      from: '/leader-preview',
      to: '/family-name-assist',
    },
    {
      jest: 'old-leader-preview-steps-pages-to-new-fna',
      from: '/leader-preview/:step(invite|enter-and-print|enable|additional-resources)',
      to: '/family-name-assist',
    },
    {
      jest: 'old-helper-preview-page-to-new-fna',
      from: '/helper-preview',
      to: '/family-name-assist',
    },
    {
      jest: 'old-helper-preview-steps-pages-to-new-fna',
      from: '/helper-preview/:step(invite|enter-and-print|enable|additional-resources)',
      to: '/family-name-assist',
    },
    {
      jest: 'old-member-preview-page-to-new-fna-for-members',
      from: '/member-preview',
      to: '/family-name-assist/members',
    },
    {
      jest: 'old-member-preview-steps-pages-to-new-fna-for-members',
      from: '/member-preview/:step(access|print|attend)',
      to: '/family-name-assist/members',
    },
  ]
  // #endregion

  return (
    <ErrorBoundary>
      <TryDemoWelcomeOverlay overlay={tryDemoWelcomeOverlay} />
      <Title>{tabText}</Title>
      <Header hideFSHF={hideFSHF} setHideFSHF={setHideFSHF} />
      <FnaUsabilla />
      <Suspense fallback={<PageSpinner />}>
        <Switch>
          {/* --------------------------------------------------- */}
          {/* Redirects from old pages to their replacement pages */}
          {/* --------------------------------------------------- */}
          {redirectsConfig.map(({ jest, from, to }) => (
            <Redirect exact key={jest} jest={jest} from={from} to={{ ...location, pathname: to }} />
          ))}

          {/* NOTE: redirection logic for subjoined_paths is handled in the server.js file's snowApp code. */}
          <CustomAuthRoute
            exact
            path={[
              '/',
              `/:page(add-name)/:step(${[...addNameFlowSteps, ...ordinancesReadySteps].join('|')})?`,
              `/:page(account)/:step(${accountSetupFlowSteps.join('|')})?`,
            ]}
          >
            <FtteProvider>
              <FamilyNameAssist />
            </FtteProvider>
          </CustomAuthRoute>

          {/* Temple FOR QR Code Page  */}
          <Route exact path="/:page(scan)/:forCode" component={QrScan} />

          {/* Verify Account Page */}
          <Route exact path="/verify-account" component={VerifyAccount} />

          {/* New Preview Pages */}
          <Route exact path="/family-name-assist" component={LandingPreview} />
          <Route exact path="/family-name-assist/members" component={MemberPreview} />
          <Route exact path="/family-name-assist/get-started" component={GetStarted} />
          <Route exact path="/family-name-assist/get-started/helpers/step1" component={GetStartedHelper1} />
          <Route exact path="/family-name-assist/get-started/helpers/step2" component={GetStartedHelper2} />

          {/* PDF Pages */}
          <Route exact path="/pdf/account-setup" component={MemberInstructions} />
          <Route exact path="/pdf/name-cards" component={NameCards} />
          <Route exact path="/pdf/planning-considerations" component={PlanningConsiderations} />

          {/* LCR Auth Components */}
          <Route exact path="/LCR/:memberUuid">
            {/* NOTE: We pass in `isIntegration` for ease of testing. */}
            <LcrAuthSwitch isIntegration={isIntegration} />
          </Route>

          {/* LCR Demo Pages */}
          <Route exact path="/lcr-demo/:page(dashboard|temple-recommend|fna)" component={LcrDemo} />
          <Route exact path="/choose-your-demo" component={ChooseYourDemo} />

          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  )
}
export default App
